/**
 * #453 碳中和数据仪表板 (Carbon Neutrality Dashboard)
 * 
 * 实现时间：2026-03-08 09:35
 * 来源：2026 ESG 趋势 - 碳中和/可持续发展数据可视化
 * 类型：数据可视化 + 环保主题 + 仪表板
 * 
 * 技术要点：
 * 1. 碳排放环形进度条（3 个：交通、能源、工业）
 * 2. 碳中和路径图表（折线图 + 预测）
 * 3. 实时碳足迹数据面板
 * 4. 环保指标统计卡片（4 个）
 * 5. Spring 物理进入动画
 * 6. 绿色主题配色（绿/青/蓝色系）
 * 7. 装饰粒子系统（20 个）
 * 8. 趋势箭头动画
 * 
 * 视觉效果：环保、数据感、专业、现代
 * 适用场景：ESG 报告、企业可持续发展、政府环保展示、碳中和宣传
 * 
 * 实现难度：⭐⭐⭐ (中等)
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

export interface CarbonEmissionItem {
  name: string;
  value: number;
  target: number;
  color: string;
}

export interface YearlyEmissionItem {
  year: number;
  emission: number;
}

export interface StatCardItem {
  label: string;
  value: string;
  trend: 'up' | 'down' | 'target';
  color: string;
}

export interface CarbonNeutralityDashboardProps {
  title?: string;
  subtitle?: string;
  chartTitle?: string;
  footerText?: string;
  carbonData?: CarbonEmissionItem[];
  yearlyData?: YearlyEmissionItem[];
  stats?: StatCardItem[];
}

export const defaultCarbonNeutralityDashboardProps: Required<CarbonNeutralityDashboardProps> = {
  title: '碳中和数据仪表板',
  subtitle: 'Carbon Neutrality Dashboard',
  chartTitle: '碳中和路径',
  footerText: '数据来源：企业可持续发展报告 | 更新时间：2026-03-08',
  carbonData: [
    { name: '交通', value: 45, target: 20, color: '#10b981' },
    { name: '能源', value: 62, target: 35, color: '#06b6d4' },
    { name: '工业', value: 38, target: 15, color: '#8b5cf6' },
  ],
  yearlyData: [
    { year: 2020, emission: 100 },
    { year: 2021, emission: 92 },
    { year: 2022, emission: 85 },
    { year: 2023, emission: 78 },
    { year: 2024, emission: 70 },
    { year: 2025, emission: 60 },
    { year: 2026, emission: 52 },
  ],
  stats: [
    { label: '碳排放减少', value: '-48%', trend: 'down', color: '#10b981' },
    { label: '可再生能源', value: '76%', trend: 'up', color: '#06b6d4' },
    { label: '碳中和目标', value: '2030', trend: 'target', color: '#8b5cf6' },
    { label: '植树造林', value: '125K', trend: 'up', color: '#f59e0b' },
  ],
};

export const CarbonNeutralityDashboard: React.FC<CarbonNeutralityDashboardProps> = ({
  title = defaultCarbonNeutralityDashboardProps.title,
  subtitle = defaultCarbonNeutralityDashboardProps.subtitle,
  chartTitle = defaultCarbonNeutralityDashboardProps.chartTitle,
  footerText = defaultCarbonNeutralityDashboardProps.footerText,
  carbonData = defaultCarbonNeutralityDashboardProps.carbonData,
  yearlyData = defaultCarbonNeutralityDashboardProps.yearlyData,
  stats = defaultCarbonNeutralityDashboardProps.stats,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // ========== 1. Spring 物理进入动画 ==========
  const titleSpring = spring({
    frame,
    fps,
    config: { damping: 200 },
  });

  const cardSpring = spring({
    frame: frame - 20,
    fps,
    config: { damping: 200 },
  });

  const ringProgress = interpolate(frame, [30, 120], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // ========== 2. 碳中和路径折线图 ==========
  const chartWidth = 700;
  const chartHeight = 250;
  const chartX = width / 2 + 50;
  const chartY = height / 2 - 100;
  const padding = 40;

  const maxEmission = Math.max(...yearlyData.map((d) => d.emission));
  const minEmission = Math.min(...yearlyData.map((d) => d.emission));

  const linePath = yearlyData
    .map((data, idx) => {
      const x = chartX + padding + (idx / (yearlyData.length - 1)) * (chartWidth - padding * 2);
      const y = chartY + chartHeight - padding - ((data.emission - minEmission) / (maxEmission - minEmission)) * (chartHeight - padding * 2);
      return idx === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    })
    .join(' ');

  const chartOpacity = interpolate(frame, [60, 80], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const statsOpacity = interpolate(frame, [80, 100], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // ========== 5. 装饰粒子系统（20 个粒子）==========
  const particles = Array.from({ length: 20 }, (_, i) => {
    const angle = (i * Math.PI * 2) / 20 + frame * 0.015;
    const radius = 400 + Math.sin(frame * 0.03 + i) * 30;
    const x = width / 2 + Math.cos(angle) * radius;
    const y = height / 2 + Math.sin(angle) * radius;

    const particleOpacity = interpolate(
      frame,
      [i * 2, i * 2 + 10],
      [0, 0.4],
      { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
    );

    const size = 4 + Math.sin(frame * 0.08 + i) * 2;

    return { x, y, opacity: particleOpacity, size };
  });

  // ========== 6. 数字滚动效果 ==========
  const numberRoll = (target: number, progress: number) => {
    return Math.floor(target * progress);
  };

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #064e3b 0%, #0d5f4d 50%, #065f46 100%)',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* 背景网格装饰 */}
      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', opacity: 0.08 }}
      >
        {Array.from({ length: 20 }).map((_, i) => (
          <line
            key={`v-${i}`}
            x1={i * 100}
            y1={0}
            x2={i * 100}
            y2={height}
            stroke="#10b981"
            strokeWidth={1}
          />
        ))}
        {Array.from({ length: 12 }).map((_, i) => (
          <line
            key={`h-${i}`}
            x1={0}
            y1={i * 100}
            x2={width}
            y2={i * 100}
            stroke="#10b981"
            strokeWidth={1}
          />
        ))}
      </svg>

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 0,
          right: 0,
          textAlign: 'center',
          transform: `translateY(${interpolate(titleSpring, [0, 1], [-50, 0])}px)`,
          opacity: titleSpring,
        }}
      >
        <h1
          style={{
            fontSize: 72,
            fontWeight: 'bold',
            color: '#fff',
            margin: 0,
            textShadow: '0 0 30px rgba(16, 185, 129, 0.8)',
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: 30,
            color: '#6ee7b7',
            marginTop: 15,
          }}
        >
          {subtitle}
        </p>
      </div>

      {/* 碳排放环形进度条（3 个）*/}
      <div style={{ position: 'absolute', top: 250, left: 100, display: 'flex', gap: 60 }}>
        {carbonData.map((data, idx) => {
          const radius = 70;
          const circumference = 2 * Math.PI * radius;
          const currentProgress = (data.value / 100) * ringProgress;
          const targetProgress = data.target / 100;
          const strokeDashoffset = circumference * (1 - currentProgress);

          return (
            <div
              key={idx}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transform: `scale(${cardSpring})`,
                opacity: cardSpring,
              }}
            >
              <svg width={180} height={180} style={{ transform: 'rotate(-90deg)' }}>
                {/* 背景圆 */}
                <circle
                  cx={90}
                  cy={90}
                  r={radius}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.1)"
                  strokeWidth={12}
                />
                {/* 当前值 */}
                <circle
                  cx={90}
                  cy={90}
                  r={radius}
                  fill="none"
                  stroke={data.color}
                  strokeWidth={12}
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                />
                {/* 目标线 */}
                <circle
                  cx={90}
                  cy={90}
                  r={radius}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth={2}
                  strokeDasharray={`${circumference * targetProgress} ${circumference * (1 - targetProgress)}`}
                />
              </svg>
              <div style={{ marginTop: 15, textAlign: 'center' }}>
                <p style={{ color: '#fff', fontSize: 34, fontWeight: 'bold', margin: 0 }}>
                  {numberRoll(data.value, ringProgress)}
                </p>
                <p style={{ color: '#6ee7b7', fontSize: 24, marginTop: 5 }}>{data.name}</p>
                <p style={{ color: '#9ca3af', fontSize: 26, marginTop: 3 }}>目标: {data.target}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* 碳中和路径折线图 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        <g style={{ opacity: chartOpacity }}>
          {/* 图表背景 */}
          <rect
            x={chartX}
            y={chartY}
            width={chartWidth}
            height={chartHeight}
            fill="rgba(255, 255, 255, 0.05)"
            rx={10}
          />

          {/* 网格线 */}
          {Array.from({ length: 5 }).map((_, i) => {
            const y = chartY + padding + (i / 4) * (chartHeight - padding * 2);
            return (
              <line
                key={`grid-${i}`}
                x1={chartX + padding}
                y1={y}
                x2={chartX + chartWidth - padding}
                y2={y}
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth={1}
              />
            );
          })}

          {/* 折线 */}
          <path
            d={linePath}
            fill="none"
            stroke="#10b981"
            strokeWidth={4}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* 数据点 */}
          {yearlyData.map((data, idx) => {
            const x = chartX + padding + (idx / (yearlyData.length - 1)) * (chartWidth - padding * 2);
            const y = chartY + chartHeight - padding - ((data.emission - minEmission) / (maxEmission - minEmission)) * (chartHeight - padding * 2);

            return (
              <g key={idx}>
                <circle cx={x} cy={y} r={6} fill="#10b981" />
                <circle cx={x} cy={y} r={3} fill="#fff" />
                {/* 年份标签 */}
                <text
                  x={x}
                  y={chartY + chartHeight - 10}
                  textAnchor="middle"
                  fill="#9ca3af"
                  fontSize={22}
                >
                  {data.year}
                </text>
                {/* 数值标签 */}
                <text
                  x={x}
                  y={y - 15}
                  textAnchor="middle"
                  fill="#10b981"
                  fontSize={24}
                  fontWeight="bold"
                >
                  {data.emission}
                </text>
              </g>
            );
          })}

          {/* 图表标题 */}
          <text
            x={chartX + chartWidth / 2}
            y={chartY - 30}
            textAnchor="middle"
            fill="#fff"
            fontSize={30}
            fontWeight="bold"
          >
            {chartTitle}
          </text>
        </g>
      </svg>

      {/* 环保指标统计卡片（4 个）*/}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 40,
          opacity: statsOpacity,
        }}
      >
        {stats.map((stat, idx) => {
          const statSpring = spring({
            frame: frame - 100 - idx * 5,
            fps,
            config: { damping: 200 },
          });

          return (
            <div
              key={idx}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                padding: '25px 35px',
                borderRadius: 15,
                border: `2px solid ${stat.color}40`,
                textAlign: 'center',
                transform: `scale(${statSpring})`,
                opacity: statSpring,
                backdropFilter: 'blur(10px)',
              }}
            >
              {/* 趋势箭头 */}
              <div style={{ marginBottom: 10 }}>
                {stat.trend === 'up' && (
                  <span style={{ color: '#10b981', fontSize: 36 }}>↑</span>
                )}
                {stat.trend === 'down' && (
                  <span style={{ color: '#10b981', fontSize: 36 }}>↓</span>
                )}
                {stat.trend === 'target' && (
                  <span style={{ color: stat.color, fontSize: 36 }}>🎯</span>
                )}
              </div>

              {/* 数值 */}
              <p style={{ color: '#fff', fontSize: 36, fontWeight: 'bold', margin: 0 }}>
                {stat.value}
              </p>

              {/* 标签 */}
              <p style={{ color: '#9ca3af', fontSize: 22, marginTop: 8 }}>{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* 装饰粒子 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {particles.map((particle, idx) => (
          <circle
            key={idx}
            cx={particle.x}
            cy={particle.y}
            r={particle.size}
            fill={`hsla(${150 + idx * 3}, 70%, 60%, ${particle.opacity})`}
          />
        ))}
      </svg>

      {/* 底部说明 */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: interpolate(frame, [120, 140], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        <p style={{ color: '#6ee7b7', fontSize: 22 }}>
          {footerText}
        </p>
      </div>
    </AbsoluteFill>
  );
};
